import React, { Component, Fragment } from "react";
import { NavLink } from "react-router-dom";
import { OperationNav } from "../../sidemenu";
import { getUserPermittedRoutesForNav, isOperationOfficer, isSuperAdmin } from "../../authGuard";
import { Sidebar } from "primereact/sidebar";
import { classNames } from "primereact/utils";
import { X } from "lucide-react";
import { getIcon } from "./iconMap";
import { useSidebarStore } from "../../stores/sidebarStore";

interface SidebarProps {
    visible: boolean;
    closeSidebar: () => void;
}

interface SidebarState {
    collapsed: boolean;
}

/** Shared helper that renders navigation items for the menu panel */
const renderNavItems = (
    items: { path?: string; to?: string; label: string; icon?: string }[],
    closeSidebar?: () => void,
) => {
    return items.map((item) => {
        const IconComponent = item.icon && getIcon(item.icon);
        const path = item.path || item.to || "";
        return (
            <li key={path} className="mb-0.5">
                <NavLink
                    to={path}
                    onClick={closeSidebar}
                    className={({ isActive }) =>
                        classNames(
                            "flex items-center space-x-2 px-3 py-2 rounded-md text-sm transition-colors !no-underline",
                            {
                                "bg-primary-600 dark:bg-primary-500 text-white font-medium":
                                    isActive,
                                "text-gray-700 dark:text-neutral-300 hover:bg-primary-50 dark:hover:bg-neutral-800 hover:text-primary-600 dark:hover:text-primary-400":
                                    !isActive,
                            },
                        )
                    }
                >
                    {({ isActive }) => (
                        <>
                            {IconComponent && (
                                <IconComponent
                                    size={18}
                                    strokeWidth={1.75}
                                    className={classNames("text-xl", {
                                        "text-white": isActive,
                                        "text-primary-600 dark:text-primary-400": !isActive,
                                    })}
                                />
                            )}
                            <span>{item.label}</span>
                        </>
                    )}
                </NavLink>
            </li>
        );
    });
};

/** Render icon-only items for the icon strip */
const renderIconStripItems = (
    items: { path?: string; to?: string; label: string; icon?: string }[],
    closeSidebar?: () => void,
) => {
    return items.map((item) => {
        const IconComponent = item.icon && getIcon(item.icon);
        const path = item.path || item.to || "";
        return (
            <NavLink
                key={path}
                to={path}
                onClick={closeSidebar}
                title={item.label}
                className={({ isActive }) =>
                    classNames(
                        "w-10 h-10 flex items-center justify-center rounded-lg transition-colors !no-underline",
                        {
                            "bg-white/60 dark:bg-neutral-700 shadow-sm": isActive,
                            "hover:bg-white/50 dark:hover:bg-neutral-700": !isActive,
                        },
                    )
                }
            >
                {IconComponent && (
                    <IconComponent size={20} strokeWidth={1.75} className="text-primary-600 dark:text-primary-400" />
                )}
            </NavLink>
        );
    });
};

const getPermittedOperationNavItems = () => {
    return OperationNav.filter((item) => {
        const path = item.to;

        if (path === "/all-transactions") {
            return isSuperAdmin() || isOperationOfficer();
        }

        if (path === "sftp-files-exchanged" || path === "/sftp-files-exchanged") {
            return isSuperAdmin();
        }

        return false;
    });
};

class Nav extends Component<SidebarProps, SidebarState> {
    private unsubscribe: (() => void) | null = null;

    constructor(props: SidebarProps) {
        super(props);
        this.state = {
            collapsed: useSidebarStore.getState().collapsed,
        };
    }

    componentDidMount() {
        this.unsubscribe = useSidebarStore.subscribe((state) => {
            this.setState({ collapsed: state.collapsed });
        });
    }

    componentWillUnmount() {
        this.unsubscribe?.();
    }

    handleToggleCollapse = () => {
        useSidebarStore.getState().toggleCollapsed();
    };

    render() {
        const { visible, closeSidebar } = this.props;
        const { collapsed } = this.state;
        const operationNavItems = getPermittedOperationNavItems();

        const navContent = (onItemClick?: () => void) => (
            <nav className="flex-1 overflow-y-auto py-2 px-3">
                <ul className="space-y-1 list-none p-0">
                    {renderNavItems(getUserPermittedRoutesForNav(), onItemClick)}
                    {operationNavItems.length > 0 && (
                        <Fragment>
                            <li className="my-3 border-t border-gray-200 dark:border-neutral-700" />
                            {renderNavItems(operationNavItems, onItemClick)}
                        </Fragment>
                    )}
                </ul>
            </nav>
        );

        return (
            <Fragment>
                {/* Mobile sidebar (drawer overlay) */}
                <Sidebar
                    visible={visible}
                    onHide={closeSidebar}
                    className="p-sidebar-sm lg:hidden"
                    pt={{
                        header: {
                            className: "!hidden",
                        },
                    }}
                >
                    <div className="flex flex-col h-full">
                        <button
                            onClick={closeSidebar}
                            className="absolute top-4 right-4 z-10 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors"
                        >
                            <X className="h-5 w-5 text-gray-600 dark:text-neutral-300" />
                        </button>
                        {navContent(closeSidebar)}
                    </div>
                </Sidebar>

                {/* Desktop sidebar — collapsed: icon strip only, expanded: full menu only */}
                <div
                    className={classNames(
                        "hidden lg:flex h-[calc(100vh-64px)] sticky top-16",
                        "bg-white dark:bg-neutral-900 border-r border-gray-200 dark:border-neutral-800 transition-all duration-300",
                    )}
                >
                    {collapsed ? (
                        /* Collapsed: icon-only strip */
                        <div className="w-14 bg-primary-50 dark:bg-neutral-800 py-4 px-1 pb-2 flex flex-col items-center transition-colors">
                            {/* All nav icons */}
                            <div className="flex flex-col items-center space-y-2 flex-1 overflow-y-auto">
                                {renderIconStripItems(getUserPermittedRoutesForNav())}
                                {operationNavItems.length > 0 && (
                                    <Fragment>
                                        <div className="w-8 border-t border-gray-200 dark:border-neutral-700 my-1" />
                                        {renderIconStripItems(operationNavItems)}
                                    </Fragment>
                                )}
                            </div>

                            {/* Expand toggle at bottom */}
                            <div className="mt-2">
                                <button
                                    type="button"
                                    onClick={this.handleToggleCollapse}
                                    className="w-10 h-10 flex items-center justify-center hover:bg-white/50 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                                    title="Expand sidebar"
                                >
                                    <span className="material-icons-outlined text-primary-600 dark:text-primary-400 text-2xl">
                                        keyboard_double_arrow_right
                                    </span>
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* Expanded: full menu panel */
                        <div className="w-56 bg-white dark:bg-neutral-900 flex flex-col transition-all duration-300">
                            {navContent()}

                            {/* Collapse toggle at bottom */}
                            <div className="px-3 py-2 border-t border-gray-200 dark:border-neutral-800">
                                <button
                                    type="button"
                                    onClick={this.handleToggleCollapse}
                                    className="w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm text-gray-500 dark:text-neutral-400 hover:bg-primary-50 dark:hover:bg-neutral-800 transition-colors"
                                    title="Collapse sidebar"
                                >
                                    <span className="material-icons-outlined text-primary-600 dark:text-primary-400 text-xl">
                                        keyboard_double_arrow_left
                                    </span>
                                    <span>Collapse</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </Fragment>
        );
    }
}

export default Nav;
